import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { updateAuthForm } from "./redux/slice/loginSlice";
import { RootState } from "./redux/Store";
import LoginPage from "./modules/login/LoginPage";
import MainDashboard from "./modules/main/MainDashboard";
import { UAParser } from "ua-parser-js";
import { FeatureAccess } from "./services/AuthService";
import AccountVerification from "./modules/login/AccountVerification";
import UpdatePassword from "./modules/login/UpdatePassword";
import { useLocation } from "react-router-dom";

interface AuthState {
  token: string | null;
  isSuccessful: boolean;
  userid: string | null;
  username: string | null;
  firstname: string | null;
  lastname: string | null;
}

interface LoginSuccessData {
  token: string;
  isSuccessful: boolean;
  userid: string;
  username: string;
  firstname: string;
  lastname: string;
  userroledescription: string;
}

function App() {
  const authState = useSelector((state: RootState) => state.auth) as AuthState;
  const dispatch = useDispatch();
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState<string>("Unknown Device");
  const [tokenName, setTokenName] = useState<string | null>(null);
  const [accessibleFeatures, setAccessibleFeatures] = useState<string[]>([]);
  const location = useLocation();
  // Track the timeout timer
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const initializeApp = async () => {
      const storedToken = localStorage.getItem("token");
      const storedUserid = localStorage.getItem("userid");
      const storedUsername = localStorage.getItem("username");
      const storedFirstname = localStorage.getItem("firstname");
      const storedLastname = localStorage.getItem("lastname");

      if (storedToken && storedUserid && storedUsername && storedFirstname) {
        setTokenName(storedToken);

        dispatch(
          updateAuthForm({
            token: storedToken,
            userid: Number(storedUserid), // Keep as string to match localStorage
            username: storedUsername,
            firstname: storedFirstname,
            lastname: storedLastname,
            isSuccessful: true,
          })
        );
      }

      // Get Device ID
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const currentDeviceId = result.visitorId;
      getDeviceId().then((deviceId) => {
        setDeviceId(deviceId);
        localStorage.setItem("deviceId", deviceId);
      });

      // Detect Device Type
      const parser = new UAParser();
      const device = parser.getDevice();
      const userAgent = navigator.userAgent.toLowerCase();

      let deviceType = "Unknown Device";
      if (device.type === "mobile") {
        deviceType = userAgent.includes("iphone") ? "iPhone" : "Android";
      } else if (device.type === "tablet") {
        deviceType = "Tablet";
      } else if (!device.type) {
        deviceType =
          userAgent.includes("macintosh") || userAgent.includes("windows")
            ? "Laptop"
            : "Desktop";
      }

      setDeviceName(deviceType);
      localStorage.setItem("deviceType", deviceType);
    };

    initializeApp();

    // --- SESSION TIMEOUT LOGIC ---
    const handleUserActivity = () => {
      localStorage.setItem("lastActivity", new Date().toISOString());

      if (timerId) {
        clearTimeout(timerId);
      }

      const newTimerId = setTimeout(
        () => {
          localStorage.removeItem("token");
          localStorage.removeItem("userid");
          localStorage.removeItem("username");
          localStorage.removeItem("firstname");
          localStorage.removeItem("lastname");
          // localStorage.removeItem('advancedSearchForm');

          setTokenName(null);
          dispatch(updateAuthForm({ token: null, isSuccessful: false }));
        },
        30 * 60 * 1000
      );

      setTimerId(newTimerId);
    };

    return () => {
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [dispatch, timerId]);

  const handleLoginSuccess = (data: LoginSuccessData) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("userid", data.userid);
    localStorage.setItem("username", data.username);
    localStorage.setItem("firstname", data.firstname);
    localStorage.setItem("lastname", data.lastname);
    localStorage.setItem("userrole", data.userroledescription);

    setTokenName(data.token);

    dispatch(
      updateAuthForm({
        ...data,
        isSuccessful: true,
        token: data.token,
        userid: Number(data.userid),
        username: data.username,
        firstname: data.firstname,
        lastname: data.lastname,
      })
    );
  };
  const getDeviceId = async () => {
    // Check if a device ID is already stored in localStorage
    let storedDeviceId = localStorage.getItem("deviceId");

    if (storedDeviceId) {
      return storedDeviceId; // Reuse existing ID
    }

    // Generate a new fingerprint if no stored ID exists
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const newDeviceId = result.visitorId;

    // Store the generated ID for future use
    localStorage.setItem("deviceId", newDeviceId);
    return newDeviceId;
  };

  const isVerificationPage = location.pathname.startsWith(
    "/account-verification"
  );
  const isUpdatePasswordPage = location.pathname.startsWith("/updatePassword");

  return (
    <div className="h-100">
      {isVerificationPage ? (
        <AccountVerification />
      ) : isUpdatePasswordPage ? (
        <UpdatePassword />
      ) : authState.isSuccessful ? (
        <MainDashboard
          accessibleFeatures={accessibleFeatures}
          userid={authState.userid}
          username={authState.username}
          firstname={authState.firstname}
          lastname={authState.lastname}
        />
      ) : (
        <div className="h-100">
          <LoginPage
            deviceId={deviceId}
            deviceName={deviceName}
            onLoginSuccess={handleLoginSuccess}
          />
        </div>
      )}
    </div>
  );
}

export default App;
